import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";

import { displayFormatDate } from "../../../../../utils/helpers.js";
import { Button } from "../../../ui/button/button.tsx";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../ui/responsive-table/index.ts";
import { EmptyStateMessage } from "../../../shared/empty-state-message/empty-state-message.tsx";
import { Card } from "../../../shared/card/card.tsx";
import type { OnboardInvitationTableRow } from "../schemas/onboard-invitation-row.schema.ts";
import { usePostJoinFormList } from "../hooks/use-post-join-form-list.ts";
import { InvitePostJoinModal } from "../modals/invite-post-join-modal.tsx";

import styles from "./post-join-form-tab.module.css";
import { Badge } from "../../../ui/index.ts";

export function PostJoinFormTab({ active = true }: { active?: boolean }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const { rows, loading, error, refetch } = usePostJoinFormList();

  const tableRows: OnboardInvitationTableRow[] = useMemo(
    () =>
      rows.map((r, i) => {
        const rec = r as Record<string, unknown>;
        const phase2Id = typeof rec.phase2Id === "number" ? rec.phase2Id : i;
        const employeeId = typeof rec.employeeId === "number" ? rec.employeeId : i;
        return {
          ...r,
          id: `post-join-${String(phase2Id)}-${String(employeeId)}`,
        };
      }),
    [rows],
  );

  const columns = useMemo<ResponsiveColumn<OnboardInvitationTableRow>[]>(
    () => [
      {
        id: "name",
        header: "Employee Name",
        accessor: "name",
        sortable: true,
        truncate: true,
      },
      {
        id: "email",
        header: "Email",
        accessor: "email",
        sortable: true,
        truncate: true,
      },
      {
        id: "createdOn",
        header: "Invitation Date",
        accessor: "createdOn",
        sortable: true,
        render: (row) => (
          <span className="body-sm">
            {displayFormatDate(row.createdOn ?? "")}
          </span>
        ),
        getSortValue: (row) => new Date(row.createdOn ?? 0).getTime(),
        getTitleText: (row) => displayFormatDate(row.createdOn ?? ""),
      },
      {
        id: "status",
        header: "Status",
        accessor: "status",
        sortable: true,
        nowrap: true,
        render: (_) => {
          return <Badge variant={"warning"}>Pending</Badge>;
        },
      },
    ],
    [],
  );

  const totalElements = rows.length;

  return (
    <div className={styles.root}>
      <Card className={styles.toolbarCard}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarRight}>
            {active && <Button type="button" variant="accent" size="md" onClick={() => setInviteOpen(true)}>
              Invite Employee
            </Button>}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className={styles.loaderWrap} aria-busy="true" aria-label="Loading post-join forms">
          <TailSpin height={56} width={56} color="var(--color-accent-500, #2563eb)" visible />
        </div>
      ) : (
        <ResponsiveDataTable<OnboardInvitationTableRow>
          serialNumber
          pagination={false}
          columns={columns}
          data={tableRows}
          defaultSortKey="createdOn"
          defaultSortDir="desc"
          aria-label="Post-join forms"
          totalLabel={`Total Invites: ${totalElements}`}
          emptyState={<EmptyStateMessage message="No records available." />}
        />
      )}

      <InvitePostJoinModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => void refetch()}
      />
    </div>
  );
}
